import TenantCategoryForm from './components/settings/TenantCategoryForm';
import './App.css';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import AppContext, {
  IDetailsListBasicExampleItem,
  IMessageBarAction,
  IMiniChartOptions,
} from './components/AppContext';
import ShowQR from './components/ShowQR';
import Asset from './components/assets/Asset';
import Home from './components/Home';
import { FC, ReactNode, useState } from 'react';
import ImportAsset from './components/import/ImportAsset';
import Profile from './components/Profile';
import { MessageBarType } from '@fluentui/react';
import { loginRequest } from './authConfig';
import ItemsList from './components/assets/AssetsListV2';
import QrCodes from './components/qr/QrCodes';
import Settings from './components/settings/Settings';
import Issues from './components/issues/Issues';
import Zones from './components/zones/Zones';
import Categories from './components/Categories';
import ImportIssueTypes from './components/import/ImportIssueTypes';
import ImportZones from './components/import/ImportZones';
import Issue from './components/issues/Issue';
import PermissionsPivot from './components/security/Security';
import ReportsViewer from './components/reports/ReportsViewer';
import ReportTemplatesList from './components/reportTemplates/ReportTemplatesList';
import ReportTemplateEditor from './components/reportTemplates/ReportTemplateEditor';
import { Metadata } from './components/settings/Metadata';
import MetadataList from './components/settings/MetadataList';
import AssetsChanges from './components/assets/AssetsChanges';
import Tenant from './components/security/Tenant';
import Group from './components/security/Group';
import User from './components/security/User';
import AccessRuleEdit from './components/security/AccessRuleEdit';
import QrCodesTemplates from './components/qr/QrCodesTemplates';
import ReferenceValue from './components/settings/ReferenceValue';
import LabelValue from './components/settings/LabelValue';
import PricingItemForm from './components/settings/PricingItemForm';
import PricingRateForm from './components/settings/PricingRateForm';
import BillingClassForm from './components/settings/BillingClassForm';
import AutomaticSurchargeForm from './components/settings/AutomaticSurchargeForm';
import TenantSurchargeForm from './components/settings/TenantSurchargeForm';
import CompanyHolidayForm from './components/settings/CompanyHolidayForm';
import TimeServiceForm from './components/settings/TimeServiceForm';
import Workflow from './components/settings/Workflow';
import Workflows from './components/settings/Workflows';
import Dashboard from './components/dashboard/Dashboard';
import WorksV2 from './components/works/WorksV2';
import Work from './components/works/Work';
import ContractorsV2 from './components/contractors/ContractorsV2';
import ContractorV2 from './components/contractors/ContractorV2';
import Calendar from './components/calendar/Calendar';
import Renters from './components/renters/Renters';
import Renter from './components/renters/Renter';
import Rents from './components/renters/Rents';
import Rent from './components/renters/Rent';
import WorkflowExecs from './components/settings/WorkflowExecs';
import WorkflowExec from './components/settings/WorkflowExec';
import Status from './components/settings/Status';
import Report from './components/settings/Report';
import { ApolloProvider } from '@apollo/client';
import { client } from './services/apolloClient';
import Orders from './components/orders/Orders';
import Order from './components/orders/Order';
import { webLightTheme } from '@fluentui/react-components';
import { Features, FeatureFlagRedirect } from './libs/featureAccess';
import { TelemetryProvider, MokoThemeProvider } from '@moko/ui';
import { FeatureFlagProvider } from '@moko/utils/src/featureAccess';
import { useAtomValue } from 'jotai';
import { tenantInfoAtom } from './atoms/tenantAtom';
import { signOut } from './services/authHelper';
import { useAccessStatus } from './auth/useAccessStatus';
import { useUserLabels } from './auth/useUserLabels';
import {
  CheckingAccessScreen,
  NoAccessScreen,
  SignInScreen,
} from './auth/AuthScreens';

interface RouteConfig {
  path: string;
  element: ReactNode;
  featureKey?: Features;
}

const AuthorizedRoot: FC<{ children: ReactNode }> = ({ children }) => {
  const tenantInfo = useAtomValue(tenantInfoAtom);
  return (
    <FeatureFlagProvider
      otherInfo={tenantInfo}
      isTenantReady={tenantInfo !== undefined}
    >
      {children}
    </FeatureFlagProvider>
  );
};

const routeConfig: RouteConfig[] = [
  { path: '/', element: <Dashboard /> },
  {
    path: '/assets',
    element: <ItemsList />,
    featureKey: Features.FEATURE_ASSETS,
  },
  {
    path: '/assetAudit',
    element: <AssetsChanges />,
    featureKey: Features.FEATURE_ASSETS,
  },
  {
    path: '/newAsset',
    element: <Asset />,
    featureKey: Features.FEATURE_ASSETS,
  },
  {
    path: '/editAsset',
    element: <Asset />,
    featureKey: Features.FEATURE_ASSETS,
  },
  { path: '/zones', element: <Zones />, featureKey: Features.FEATURE_ZONES },
  {
    path: '/categories',
    element: <Categories />,
    featureKey: Features.FEATURE_CATEGORIES,
  },
  { path: '/renters', element: <Renters />, featureKey: Features.FEATURE_RENT },
  {
    path: '/newRenter',
    element: <Renter />,
    featureKey: Features.FEATURE_RENT,
  },
  {
    path: '/editRenter',
    element: <Renter />,
    featureKey: Features.FEATURE_RENT,
  },
  { path: '/rents', element: <Rents />, featureKey: Features.FEATURE_RENT },
  { path: '/newRent', element: <Rent />, featureKey: Features.FEATURE_RENT },
  { path: '/editRent', element: <Rent />, featureKey: Features.FEATURE_RENT },
  {
    path: '/contractors',
    element: <ContractorsV2 />,
    featureKey: Features.FEATURE_CONTRACTORS,
  },
  {
    path: '/newContractor',
    element: <ContractorV2 />,
    featureKey: Features.FEATURE_CONTRACTORS,
  },
  {
    path: '/editContractor',
    element: <ContractorV2 />,
    featureKey: Features.FEATURE_CONTRACTORS,
  },
  { path: '/showQR', element: <ShowQR /> },
  { path: '/issues', element: <Issues />, featureKey: Features.FEATURE_ISSUES },
  {
    path: '/newIssue/:by',
    element: <Issue />,
    featureKey: Features.FEATURE_ISSUES,
  },
  {
    path: '/editIssue',
    element: <Issue />,
    featureKey: Features.FEATURE_ISSUES,
  },
  { path: '/importAssets', element: <ImportAsset /> },
  { path: '/importIssueTypes', element: <ImportIssueTypes /> },
  { path: '/importZones', element: <ImportZones /> },
  {
    path: '/works',
    element: <WorksV2 />,
    featureKey: Features.FEATURE_WORK_ORDERS,
  },
  {
    path: '/newWork',
    element: <Work />,
    featureKey: Features.FEATURE_WORK_ORDERS,
  },
  {
    path: '/editWork',
    element: <Work />,
    featureKey: Features.FEATURE_WORK_ORDERS,
  },
  { path: '/orders', element: <Orders />, featureKey: Features.FEATURE_ORDERS },
  {
    path: '/newOrder',
    element: <Order />,
    featureKey: Features.FEATURE_ORDERS,
  },
  {
    path: '/editOrder',
    element: <Order />,
    featureKey: Features.FEATURE_ORDERS,
  },
  {
    path: '/calendar',
    element: <Calendar />,
    featureKey: Features.FEATURE_CALENDAR,
  },
  {
    path: '/qrCodes',
    element: <QrCodes />,
    featureKey: Features.FEATURE_QR_CODES,
  },
  {
    path: '/qrCodeTemplates',
    element: <QrCodesTemplates />,
    featureKey: Features.FEATURE_QR_CODES,
  },
  { path: '/security', element: <PermissionsPivot /> },
  { path: '/newTenant', element: <Tenant /> },
  { path: '/editTenant', element: <Tenant /> },
  { path: '/newGroup', element: <Group /> },
  { path: '/editGroup', element: <Group /> },
  { path: '/newUser', element: <User /> },
  { path: '/editUser', element: <User /> },
  { path: '/newWorkAccess', element: <AccessRuleEdit entity="Work" /> },
  { path: '/editWorkAccess', element: <AccessRuleEdit entity="Work" /> },
  { path: '/newOrderAccess', element: <AccessRuleEdit entity="Order" /> },
  { path: '/editOrderAccess', element: <AccessRuleEdit entity="Order" /> },
  {
    path: '/workflows',
    element: <Workflows />,
    featureKey: Features.FEATURE_WORKFLOWS,
  },
  {
    path: '/workflows/:tab',
    element: <Workflows />,
    featureKey: Features.FEATURE_WORKFLOWS,
  },
  {
    path: '/newWorkflow',
    element: <Workflow />,
    featureKey: Features.FEATURE_WORKFLOWS,
  },
  {
    path: '/editWorkflow',
    element: <Workflow />,
    featureKey: Features.FEATURE_WORKFLOWS,
  },
  {
    path: '/workflowExecs',
    element: <WorkflowExecs />,
    featureKey: Features.FEATURE_WORKFLOWS,
  },
  {
    path: '/showWorkflowExec',
    element: <WorkflowExec />,
    featureKey: Features.FEATURE_WORKFLOWS,
  },
  { path: '/metadata', element: <MetadataList /> },
  { path: '/metadata/new', element: <Metadata /> },
  { path: '/metadata/:entity/:name', element: <Metadata /> },
  { path: '/settings/:tab', element: <Settings /> },
  { path: '/newStatuses/:type', element: <Status /> },
  { path: '/editStatuses/:type', element: <Status /> },
  { path: '/newRefValue', element: <ReferenceValue /> },
  { path: '/editRefValue', element: <ReferenceValue /> },
  { path: '/newLabel', element: <LabelValue /> },
  { path: '/editLabel', element: <LabelValue /> },
  { path: '/newPricingItem', element: <PricingItemForm /> },
  { path: '/editPricingItem', element: <PricingItemForm /> },
  { path: '/newPricingRate', element: <PricingRateForm /> },
  { path: '/editPricingRate', element: <PricingRateForm /> },
  { path: '/newTimeService', element: <TimeServiceForm /> },
  { path: '/editTimeService', element: <TimeServiceForm /> },
  { path: '/newTenantCategory', element: <TenantCategoryForm /> },
  { path: '/editTenantCategory', element: <TenantCategoryForm /> },
  { path: '/newHoliday', element: <CompanyHolidayForm /> },
  { path: '/editHoliday', element: <CompanyHolidayForm /> },
  { path: '/newBillingClass', element: <BillingClassForm /> },
  { path: '/editBillingClass', element: <BillingClassForm /> },
  { path: '/editAutomaticSurcharge', element: <AutomaticSurchargeForm /> },
  { path: '/editTenantSurcharge', element: <TenantSurchargeForm /> },
  {
    path: '/reports',
    element: <ReportsViewer />,
    featureKey: Features.FEATURE_REPORTS,
  },
  {
    path: '/newReport',
    element: <Report />,
    featureKey: Features.FEATURE_REPORTS,
  },
  {
    path: '/editReport',
    element: <Report />,
    featureKey: Features.FEATURE_REPORTS,
  },
  { path: '/reportTemplates', element: <ReportTemplatesList /> },
  { path: '/newReportTemplate', element: <ReportTemplateEditor mode="new" /> },
  {
    path: '/editReportTemplate',
    element: <ReportTemplateEditor mode="edit" />,
  },
  {
    path: '/duplicateReportTemplate',
    element: <ReportTemplateEditor mode="duplicate" />,
  },
];

const App: FC<any> = ({ instance }) => {
  const defaultItem: IDetailsListBasicExampleItem = {
    id: 0,
    uuid: '',
    code: '',
    name: '',
  };

  interface IMessage {
    message: string;
    messageType: MessageBarType;
    actions?: IMessageBarAction[];
  }

  const [selectedItem, setSelectedItem] =
    useState<IDetailsListBasicExampleItem>(defaultItem);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [message] = useState<IMessage>({
    message: '',
    messageType: MessageBarType.info,
    actions: [],
  });
  const [selectedTab, setSelectedTab] = useState('');
  const [miniChartData, setMiniChartData] = useState<Map<string, number>>();
  const [miniChartOptions, setMiniChartOptions] = useState<IMiniChartOptions>();
  const context = {
    selectedItem: selectedItem,
    setSelectedItem: (item: IDetailsListBasicExampleItem) => {
      setSelectedItem(item);
    },
    isImportDialogOpen: isImportDialogOpen,
    setIsImportDialogOpen: (flag: boolean) => {
      setIsImportDialogOpen(flag);
    },
    isProfileDialogOpen: isProfileDialogOpen,
    setIsProfileDialogOpen: (flag: boolean) => {
      setIsProfileDialogOpen(flag);
    },
    actions: message.actions ?? [],
    setSelectedTab: (
      selectedTab: string,
      miniChartData?: Map<string, number>,
      miniChartOptions?: IMiniChartOptions
    ) => {
      setSelectedTab(selectedTab);
      setMiniChartData(miniChartData ?? new Map<string, number>());
      setMiniChartOptions(miniChartOptions);
    },
    selectedTab: selectedTab,
    miniChartData: miniChartData ?? new Map<string, number>(),
    miniChartOptions: miniChartOptions ?? { xAxisFormat: (x: string) => x },
  };
  const handleSignIn = () => {
    instance.loginRedirect(loginRequest).catch((e: any) => {
      console.error(e);
    });
  };
  const handleSignOut = () => {
    signOut(instance);
  };

  const accessStatus = useAccessStatus();
  useUserLabels(accessStatus === 'granted');

  const renderByStatus = () => {
    switch (accessStatus) {
      case 'signedOut':
        return <SignInScreen onSignIn={handleSignIn} />;
      case 'checkingAccess':
      case 'bootstrapping':
        return <CheckingAccessScreen />;
      case 'noAccess':
        return <NoAccessScreen onSignOut={handleSignOut} />;
      case 'granted':
        return (
          <>
            <BrowserRouter basename="/">
              <TelemetryProvider
                appName="portal"
                connectionString={
                  import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING
                }
              >
                <AuthorizedRoot>
                  <Home>
                    <Routes>
                      {routeConfig.map(({ path, element, featureKey }) => (
                        <Route
                          key={path}
                          path={path}
                          element={
                            featureKey ? (
                              <FeatureFlagRedirect featureKey={featureKey}>
                                {element}
                              </FeatureFlagRedirect>
                            ) : (
                              element
                            )
                          }
                        />
                      ))}
                    </Routes>
                  </Home>
                </AuthorizedRoot>
              </TelemetryProvider>
            </BrowserRouter>
            <Profile />
          </>
        );
    }
  };

  return (
    <MokoThemeProvider theme={webLightTheme}>
      <AppContext.Provider value={context}>
        <ApolloProvider client={client}>{renderByStatus()}</ApolloProvider>
      </AppContext.Provider>
    </MokoThemeProvider>
  );
};

export default App;
